import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Images, Loader2, Trash2, FileText, Check } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { resolveMedia, isVideoUrl } from '../../lib/media';

const fmtSize = (b) => (!b ? '' : b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

const Thumb = ({ f }) => {
  const url = resolveMedia(f.url);
  if ((f.content_type || '').startsWith('video/') || isVideoUrl(f.url)) {
    return <video src={url} className="w-full h-full object-cover" muted playsInline preload="metadata" />;
  }
  if ((f.content_type || '').startsWith('image/')) {
    return <img src={url} alt={f.original_filename || ''} className="w-full h-full object-contain p-1" loading="lazy" />;
  }
  return <div className="w-full h-full flex items-center justify-center text-gray-500"><FileText size={22} /></div>;
};

const MediaLibraryDialog = ({ onSelect, accept = 'all', idx }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);

  const pin = () => sessionStorage.getItem('tp_admin_pin') || '';

  const load = async () => {
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/files?pin=${encodeURIComponent(pin())}`);
      if (!r.ok) throw new Error('fail');
      const d = await r.json();
      setItems(d.items || []);
    } catch {
      setItems([]);
      toast({ title: 'Errore', description: 'Impossibile caricare la libreria', variant: 'destructive' });
    }
  };

  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (e, f) => {
    e.stopPropagation();
    if (!window.confirm(`Eliminare "${f.original_filename || f.filename}" dalla libreria?`)) return;
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/files/${f.filename}?pin=${encodeURIComponent(pin())}`, { method: 'DELETE' });
    if (r.ok) setItems(items.filter(i => i.filename !== f.filename));
  };

  const visible = (items || []).filter(f => {
    const ct = f.content_type || '';
    if (accept === 'image') return ct.startsWith('image/');
    if (accept === 'video') return ct.startsWith('video/') || isVideoUrl(f.url);
    return ct.startsWith('image/') || ct.startsWith('video/') || isVideoUrl(f.url);
  });

  return (
    <>
      <Button type="button" size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`library-btn-${idx}`}
        className="border-gray-700 text-white hover:bg-gray-800 text-xs h-9 whitespace-nowrap">
        <Images size={13} className="mr-1" /> LIBRERIA
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-[95vw] sm:max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="media-library-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '2px' }}>
              LIBRERIA MEDIA {items && <span className="text-gray-500 text-sm ml-2">({visible.length} file)</span>}
            </DialogTitle>
          </DialogHeader>
          {!items ? (
            <div className="py-12 flex justify-center text-gray-400"><Loader2 className="animate-spin" /></div>
          ) : visible.length === 0 ? (
            <p className="text-gray-500 text-sm py-8 text-center">Nessun file caricato. Usa CARICA per aggiungerne uno.</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
              {visible.map((f) => (
                <button type="button" key={f.filename} onClick={() => { onSelect(f.url); setOpen(false); }}
                  data-testid={`library-item-${f.filename}`}
                  className="group relative aspect-square rounded-lg overflow-hidden border border-gray-700 hover:border-orange-500 bg-black text-left focus:outline-none focus:ring-2 focus:ring-orange-500">
                  <Thumb f={f} />
                  <div className="absolute inset-x-0 bottom-0 bg-black/80 px-1.5 py-1">
                    <p className="text-[10px] text-white truncate">{f.original_filename || f.filename}</p>
                    <p className="text-[9px] text-gray-400">{fmtSize(f.size)}</p>
                  </div>
                  <span className="absolute top-1 left-1 bg-orange-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"><Check size={11} /></span>
                  <span role="button" tabIndex={-1} onClick={(e) => remove(e, f)} title="Elimina" data-testid={`library-delete-${f.filename}`}
                    className="absolute top-1 right-1 bg-red-700 hover:bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Trash2 size={11} />
                  </span>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MediaLibraryDialog;
